import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  CloseQuestionInput,
  QuestionStatusFilter,
  ReplyQuestionInput,
  RouteQuestionInput,
  StaffQuestion,
} from './types';

const LIVE_MS = 30_000;

function useBranchKey() {
  const { selected, canSelectAll } = useBranch();
  return selected?.id ?? (canSelectAll ? 'all' : 'none');
}

const all = ['patient-questions'] as const;
const detailKey = (id: string) => [...all, 'detail', id] as const;

/** GET /patient-questions?status: my inbox (doctor) or the branch (manager, no text). */
export function usePatientQuestions(status: QuestionStatusFilter) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: [...all, 'list', branch, status],
    queryFn: async ({ signal }) =>
      (
        await api.get<StaffQuestion[]>('/patient-questions', {
          signal,
          query: { status },
        })
      ).data,
    enabled: branch !== 'none',
    refetchInterval: LIVE_MS,
  });
}

/** GET /patient-questions/{id}: the thread (messages for the doctor only). */
export function usePatientQuestion(id: string) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: async ({ signal }) =>
      (await api.get<StaffQuestion>(`/patient-questions/${id}`, { signal }))
        .data,
    retry: false,
  });
}

function useAction<I>(id: string, action: 'reply' | 'close' | 'route') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: I) =>
      (
        await api.post<StaffQuestion>(
          `/patient-questions/${id}/${action}`,
          body,
        )
      ).data,
    onSuccess: (saved) => {
      qc.setQueryData(detailKey(id), saved);
      void qc.invalidateQueries({ queryKey: [...all, 'list'] });
      void qc.invalidateQueries({ queryKey: ['shell', 'counts'] });
    },
  });
}

/** POST …/reply {text, close?, version} (doctor). */
export const useReplyQuestion = (id: string) =>
  useAction<ReplyQuestionInput>(id, 'reply');

/** POST …/close {version} (doctor). */
export const useCloseQuestion = (id: string) =>
  useAction<CloseQuestionInput>(id, 'close');

/** POST …/route {doctorId | null, version} (manager). */
export const useRouteQuestion = (id: string) =>
  useAction<RouteQuestionInput>(id, 'route');
